<template>
  <multiselect
    v-model="selectedChannels"
    track-by="id"
    :placeholder="placeholder"
    :multiple="true"
    :options="availableChannels"
    :searchable="true"
    :allow-empty="true"
    :preserve-search="true"
    :show-labels="false"
    :hideSelected="true"
    :clearOnSelect="true"
    :closeOnSelect="true"
    :internal-search="false"
    :loading="isLoading"
    :max="maxChannels"
    :showNoOptions="true"
    :showNoResult="true"
    :disabled="disabled"
    @search-change="onSearch"
  >
    <template slot="option" slot-scope="{ option }">
      <div class="watchman-option">
        <strong>{{ option.watchman.name }}</strong>
        <br />
        <div class="display-flex text-truncate">
          <ChannelIcon 
            :type="option.type" 
            :class="validationStatusColor(option)"
          />
          <span class="text-mono ml-1">{{ option.value }}</span>
        </div>
      </div>
    </template>
    <template slot="tag" slot-scope="{ option, remove, ChannelIcon }">
      <div class="watchman-selected multiselect__tag">
        <div class="head">
          <span class="name text-truncate pr-2">{{ option.watchman.name }}</span>
          <button @click="remove(option)" class="ml-auto" aria-label="remove" type="text"></button>
        </div>
        <div class="display-flex text-truncate watchman-value" :title="option.value">
          <ChannelIcon 
            :type="option.type" 
            :class="validationStatusColor(option)"
           />
          <span class="text-mono ml-1">{{ option.value }}</span>
        </div>
      </div>
    </template>
    <template slot="maxElements">
      <span v-html="$t('profile.form.select_max_options', {max: maxChannels})"></span>
    </template>
    <template slot="noResult">
      <span>{{$t("profile.form.select_no_results")}}</span>
    </template>
    <template slot="noOptions">
      <span>{{$t("profile.form.select_no_results")}}</span>
    </template>
  </multiselect>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import API from "@/services/API";
import debounce from "lodash/debounce";
import Btn from "@/components/ui/Button2";
import ChannelIcon from "@/components/watchmen/channels/ChannelIcon.vue";
import { checkValidation } from "@/components/watchmen/check-validation.js";

export default {
  name: "ChannelMultiselect",
  components: {
    Multiselect,
    Btn,
    ChannelIcon,
  },
  props: {
    value: {
      type: Array,
      required: false,
      default: [],
    },
    placeholder: {
      type: String,
      required: false,
      default: () => window.V.$i18n.t('profile.form.add_channel_placeholder'),
    },
    channelType: {
      type: String,
      required: false,
    },
    channelValidated: {
      type: Boolean,
      required: false,
    },
    maxChannels: {
      type: Number,
      required: false,
    },
    defaultChannels: {
      type: Array,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    availableChannels: [],
    selectedChannels: [],
    isLoading: false,
  }),
  methods: {
    checkValidation: checkValidation,
    validationStatusColor: function(channel) {
      return checkValidation(channel)[0] ? 'text-success' : 'text-danger'
    },
    loadData: function (params = {}) {
      this.isLoading = true;
      axios.get(API.route(API.endpoints.channels.index), {
        params: {
          search: params.search,
          types: this.channelType,
          validated: this.channelValidated ? 1 : undefined
        }
      })
      .then(({ data: response }) => {
        this.availableChannels = response.data;
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        this.isLoading = false;
      })
    },
    onSearch: debounce(function (search) {
      this.loadData({ search });
    }, 500),
  },
  created: function () {
    if ( this.value ) {
      this.selectedChannels = this.value;
    }
    this.loadData();
  },
  watch: {
    value(newValue) {
      this.selectedChannels = newValue;
    },
    selectedChannels(newValue) {
      this.$emit( "input", newValue );
    },
  },
};
</script>

<style lang="scss">

.multiselect {
  color: var(--colors-gray-900);
}
.multiselect__tags {
  border: var(--input-border);;
  padding: 6px 40px 4px 0;
  border-radius: var(--input-radius);

  .multiselect--active & {
    border-color: var(--input-focus-border-color);
    box-shadow: var(--input-focus-shadow);
  }
}
.multiselect__tags-wrap {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  counter-reset: section;
}
.multiselect__tag {
  background: var(--colors-gray-100);
  padding: 4px 12px;
  padding-right: 25px;
  border-radius: var(--radius-md);
  margin: 0 0 4px 6px;
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  line-height: 1.5;
  width: fit-content;
  max-width: calc(100% - 8px);
  color: inherit;
  position: relative;

  &.readonly {
    background: var(--colors-gray-400);
    cursor: not-allowed;
  }

  button,
  .multiselect__tag-icon {
    color: var(--colors-gray-500);
    background: var(--colors-gray-100);
    border: none;
    display: flex;
    width: 1.5em;
    height: 1.5em;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-md);
    position: absolute;
    top: 0;
    right: 0;

    &:after {
      content: "✕";
      font-size: inherit;
      color: inherit !important;
    }

    &:hover, &:focus {
      background: var(--colors-gray-300);
      color: var(--colors-gray-900);
    }

    &:focus {
      outline: none;
    }
    &:focus-visible {
      box-shadow: var(--button-focus-shadow);
    }

  }
}

.multiselect__select,
.multiselect__spinner {
  right: 2px;
  top: 2px;
  border-radius: 8px;
  width: 40px;
}

.multiselect__placeholder {
  display: none;
}
.multiselect__input::placeholder {
  color: var(--colors-gray-400);
  font-size: inherit;
}

.multiselect__input {
  border: none !important;
  width: 100% !important;
  padding: 4px 12px !important;
  position: inherit !important;
  margin: 0;
  font-size: 1rem;
}


.multiselect__spinner::after,
.multiselect__spinner::before {
  border-top-color: var(--colors-gray-500);
}
.multiselect__content-wrapper {
  box-shadow: none;
  z-index: 1;
  border: none !important;

  .multiselect--active & {
    border: 1px solid var(--colors-gray-200) !important;
    box-shadow: var(--shadows-md);
    translate: 0 1px;
  }
  .multiselect--active.multiselect--above & {
    translate: 0 -1px;
  }
}
.multiselect__content {
  width: 100%;
  padding-block: 4px;
  max-height: none !important;
}
.multiselect__element {
  padding-inline: 4px;

  .multiselect__option {
    width: 100%;
    color: var(--colors-gray-900);
    padding: 4px 12px;
    border-radius: var(--radius-md);
    border: 1px solid var(--colors-white);
    display: flex;
    align-items: center;
    font-size: 1rem;
    overflow: hidden;

    &::after {
      display: none;
    }
  }
  .multiselect__option--selected {
    background: var(--colors-gray-100);
  }
  .multiselect__option--highlight {
    background: var(--colors-gray-200);
  }
}



.multiselect-with-index {
  max-width: 400px;

  .watchman-selected {
    width: 100%;
    padding-left: 32px;
  }

  .watchman-selected::before {
    counter-increment: section;
    content: counter(section) "";
    color: var(--colors-gray-400);
    font-weight: bold;
    font-family: var(--fonts-code);
    position: absolute;
    margin-left: -20px;
  }
}

.watchman-selected {
  padding-right: 12px;
  overflow: visible;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .name {
      font-weight: 700;
      line-height: 1.333;
    }

  }

  .watchman-value {
    font-size: 0.9em;
  }
}
</style>
